import './index.css'
import type { TooltipProps } from 'antd'
import { CheckOutlined, CloseOutlined, ReloadOutlined } from '@ant-design/icons'
import CIMSButton from 'components/CIMSButton'
import type { ApprovalActionButtonProps } from './types'

/** Keeps action clicks within the cell and loading on the initiating control. */
function ApprovalActionButton({
  action,
  onAction,
  optionId,
  optionLabel,
  pending = false,
  pendingAction = null,
  recordId,
}: ApprovalActionButtonProps) {
  const initiating =
    pendingAction?.recordId === recordId &&
    pendingAction?.optionId === optionId &&
    pendingAction.action === action.action
  const actionPending = pending || pendingAction !== null
  let reason: string | undefined
  if (actionPending && !initiating) {
    reason = 'Another approval action is being submitted.'
  } else if (!action.authorized) {
    reason = 'You are not authorized to act on this approval option.'
  }
  let disabledMessage = reason
  if (reason || initiating)
    disabledMessage = [action.actionLabel, reason].filter(Boolean).join(': ')

  let icon = <ReloadOutlined style={{ transform: 'scaleX(-1)' }} />
  if (action.action === 'approved') icon = <CheckOutlined />
  else if (action.action === 'rejected') icon = <CloseOutlined />

  let tooltipPlacement: TooltipProps['placement']
  if (action.action === 'approved' || action.action === 'cleared')
    tooltipPlacement = 'left'
  else if (action.action === 'rejected') tooltipPlacement = 'right'

  return (
    <span
      style={{
        display: 'flex',
        flex: action.action === 'cleared' ? 1 : undefined,
      }}
      onClick={(event) => event.stopPropagation()}
    >
      <CIMSButton
        aria-label={`${action.actionLabel} ${optionLabel}`}
        block={action.action === 'cleared'}
        className={
          action.action !== 'cleared' && !disabledMessage
            ? `approval-action-button-${action.action}`
            : undefined
        }
        danger={action.action === 'rejected'}
        disabled={
          disabledMessage
            ? {
                disabled: true,
                reason: {
                  message: disabledMessage,
                  why: 'disabled',
                },
              }
            : undefined
        }
        loading={initiating}
        icon={icon}
        onClick={(event) => {
          event.stopPropagation()
          if (!reason && !initiating) void onAction(optionId, action)
        }}
        size="small"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: action.action === 'cleared' ? undefined : 32,
        }}
        tooltip={{
          placement: tooltipPlacement,
          title: action.actionLabel,
        }}
        type="default"
      />
    </span>
  )
}

export default ApprovalActionButton
